import { Component, DestroyRef, OnInit, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, combineLatestWith, debounceTime, distinctUntilChanged, filter, find, map, merge, of, shareReplay, switchMap, take, takeUntil } from 'rxjs';

import { MatDialog, MatDialogState } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';

import { HoldingDiscriminator } from '../shared/enums/HoldingDiscriminator.enum';
import { SearchOptionsRequest } from '../shared/models/SearchOptionsRequest.model';
import { UnfinalizedDistributionDTO } from './shared/holding/distribution/UnfinalizedDistributionDTO.model';
import { AppQuery } from '../state/app.query';
import { RoutingService } from '../services/routing.service';
import { AppService } from '../state/app.service';
import { BankAccountsMsService } from './bankAccounts/bank-accounts-ms.service';
import { SidePanelService } from './shared/side-panel/side-panel.service';
import { Notification, NotificationManagerService } from './shared/notification-manager/notification-manager.service';
import { UnitApplicationMsServiceService } from './bankAccounts/cre/unit-application-ms-service.service';
import { BankApplicationDataService } from '../services/gp/bank-application-data.service';
import { GpDistributionDataService } from '../services/gp/gp-distribution-data.service';
import { DialogService } from '../services/dialog.service';
import { ConfirmDialogParams } from '../shared/components/confirm-dialog/confirm-dialog.component';
import { PromoDialogComponent } from './promo/promo.component';
import { TerraUtils } from '../shared/TerraUtils';

@Component({
  selector: 'terra-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  @ViewChild('sidenav', { static: true }) sidenav!: MatSidenav;
  @ViewChild('placeholder', { read: ViewContainerRef, static: true }) viewContainerRef!: ViewContainerRef;

  promo$ = this.appQuery.gpUiPrefs.pipe(map((uiState) => uiState.promo));
  constructor(
    private readonly appService: AppService,
    private readonly appQuery: AppQuery,
    private readonly dialog: MatDialog,
    private dialogService: DialogService,
    private notificationManagerService: NotificationManagerService,
    private bankAccountsMsService: BankAccountsMsService,
    private bankApplicationDataService: BankApplicationDataService,
    private gpDistributionDataService: GpDistributionDataService,
    private unitApplicationMsService: UnitApplicationMsServiceService,
    private router: Router,
    private routingService: RoutingService,
    private sidePanelService: SidePanelService
  ) {
    this.bankAccountsMsService.bankAccountsRefresh$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(_ => this.bankAccountsMsService.getDisconnectedBankAccountCount()),
      filter(disconnectedNumber => !!disconnectedNumber),
      shareReplay(1)
    ).subscribe((disconnectedNumber) => this.notificationManagerService.setNotifications("BankAccountsMsService", [{
      type: "info",
      icon: "",
      message: `<strong>${disconnectedNumber} Bank Accounts Disconnected:</strong> Please update your bank connection to access the latest transactions and balances.`,
      cta: { caption: "Reconnect", type: "link", action: this.reconnectAction.bind(this) },
      dismissible: false
    }]));

    const unitApplicationDrafts = this.bankApplicationDataService.creApplicationsUpdated$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(_ => {
        const searchOptions: SearchOptionsRequest = {
          filter: "",
          includeRemoved: false,
          sortOrder: "",
          orderBy: "",
          pageSize: 25,
          pageNumber: 0
        };
        return this.unitApplicationMsService.getUnitApplicationDrafts(searchOptions);
      }),
      shareReplay(1)
    );
    unitApplicationDrafts.subscribe((drafts) => {
      const notifications: Notification[] = drafts?.rows?.map(draft => {
        const name = (draft.name ?? "") !== "" ? draft.name : (draft.entityName ?? "") !== "" ? draft.entityName : null;
        const highlighted = name !== null ? ` for <strong>${name}</strong>` : null;
        return {
          type: "info",
          icon: "",
          message: `You have a pending bank account application${highlighted === null ? "" : highlighted}.`,
          cta: { caption: "Complete It Now", type: "link", action: this.editDraft.bind(this, draft.id) },
          dismissible: false
        }
      });
      this.notificationManagerService.setNotifications("UnitApplicationMsService", notifications);
    });
    unitApplicationDrafts.pipe(
      combineLatestWith(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))),
      filter(([_, ne]) => !ne.url.includes(TerraUtils.consts.paths.TWO_FACTOR_VERIFICATION)),
      debounceTime(3000),
      take(1),
      switchMap(([drafts]) => {
        const dialogParams = new ConfirmDialogParams();
        dialogParams.cancelLabel = `Remind Me Later`;

        if (!drafts?.rows?.length) return of();

        let applicationId: number;
        if (drafts.rows.length === 1) {
          applicationId = drafts.rows[0].id;
          dialogParams.title = `Complete Your Bank Account Application`;
          dialogParams.description = `It looks like you started a bank account application but haven’t completed it yet. Your progress has been saved as a draft. Complete it now to unlock full functionality and streamline your financial processes.`;
          dialogParams.actionLabel = `Resume Application`;
        }
        else {
          dialogParams.title = `Complete Your Bank Account Applications`;
          dialogParams.description = `It looks like you started a bank account application but haven’t completed it yet. Your progress has been saved as a draft. Complete it now to unlock full functionality and streamline your financial processes.`;
          dialogParams.actionLabel = `View Drafts`;
        }

        const navAway$ = this.router.events.pipe(
          takeUntilDestroyed(this.destroyRef),
          find((e): e is NavigationStart => e instanceof NavigationStart),
          take(1)
        );

        const dialog = this.dialogService.confirmDialog(dialogParams);

        navAway$.pipe(
          takeUntilDestroyed(this.destroyRef),
          takeUntil(dialog.beforeClosed())
        ).subscribe(() => {
          if (dialog.getState() === MatDialogState.OPEN) {
            dialog.close(false);
          }
        });

        return dialog.afterClosed().pipe(map((res) => [res, res ? applicationId : NaN] as [boolean, number]));
      })
    ).subscribe(([res, applicationId]) => {
      if (res) {
        if (applicationId)
          this.editDraft(applicationId);
        else
          this.router.navigate([this.routingService.bankAccountsPage("applications")]);
      }
    });

    this.gpDistributionDataService.gpDistributionUpdated$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => this.gpDistributionDataService.getUnfinalized()),
      combineLatestWith(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)))
    ).subscribe(([res, ne]) => {
      if (res) {
        const notifications: Notification[] = res
          .filter(distribution => !ne.url.includes(this.getDistributionURL(distribution)))
          .map(distribution => ({
            type: "info",
            icon: "",
            message: `Holding <strong>${distribution.holdingName}</strong> has a Non-Finalized Distribution.`,
            cta: { caption: "View Details", type: "link", action: this.goToDistribution.bind(this, distribution) },
            dismissible: false
          }));
        this.notificationManagerService.setNotifications("GpDistributionDataService", notifications);
      }
    });
  }

  ngOnInit(): void {
    this.sidePanelService.setSidenav(this.sidenav, this.viewContainerRef);

    combineLatest([this.appService.accountDetails$, this.promo$])
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(([accountDetails, promo]) => {
        if (promo.showCovercyAi &&
          this.differenceDays(accountDetails.createTimestamp) > 5 // TODO: move to Project Constants/Config
        ) {
          this.openPromoDialog();
        }
      });
  }

  private differenceDays(date: Date | string):number {
    return (
      (new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24)
    );
  }

  private openPromoDialog(): void {
    const dialogRef = this.dialog.open(PromoDialogComponent,
      {
        height: '756px',
        width: '700px',
        panelClass: 'promo-panel'
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      this.appService.updatePromoUIState(
        'showCovercyAi',
        false
      );
    });
  }

  reconnectAction(): void {
    this.router.navigate([this.routingService.bankAccountsPage("gp")], { queryParams: { action: 'reconnect' } });
  }

  editDraft(applicationId: number): void {
    this.router.navigate([this.routingService.bankApplicationDraftEditPage(applicationId)]);
  }

  private getDistributionURL(distribution: UnfinalizedDistributionDTO): string {
    switch (distribution.holdingDiscriminator) {
      case HoldingDiscriminator.Asset:
        return this.routingService.assetDistribution(distribution.holdingId, distribution.id);
      case HoldingDiscriminator.Fund:
        return this.routingService.fundDistribution(distribution.holdingId, distribution.id);
      default:
        console.log('Incorrect Holding Discriminator for distribution:', distribution.id);
        return undefined;
    }
  }

  goToDistribution(distribution: UnfinalizedDistributionDTO): void {
    const url = this.getDistributionURL(distribution);
    if (url) this.router.navigate([url]);
  }
}
